export const KEEP_ALIVE_MIN_INTERVAL_MS = 5 * 60 * 1000

export interface SessionKeepAlive {
  notifyActivity: () => void
  // Both also resume after a 401, since the member may just have signed in.
  notifyNavigation: () => void
  notifySignIn: () => void
}

interface SessionKeepAliveOptions {
  // Resolves false when there is no session to renew (401).
  ping: () => Promise<boolean>
  minIntervalMs?: number
  now?: () => number
}

// BFF calls don't refresh the browser's carrot-app cookie, so this pings carrot-app while the member is active.
// Activity only, never a timer, so an idle tab still reaches the 20-minute idle timeout.
export function createSessionKeepAlive({
  ping,
  minIntervalMs = KEEP_ALIVE_MIN_INTERVAL_MS,
  now = () => Date.now()
}: SessionKeepAliveOptions): SessionKeepAlive {
  let lastPingAt: number | null = null
  // After a 401, stay quiet until a route change or sign-in, so anonymous pages don't ping every interval.
  let stoppedForNoSession = false
  // A 401 that lands after a resume is stale: the member may just have signed in.
  let resumes = 0

  function send(): void {
    const at = now()
    if (lastPingAt !== null && at - lastPingAt < minIntervalMs) return
    lastPingAt = at
    const sentDuring = resumes
    ping()
      .then((sessionActive) => {
        if (!sessionActive && sentDuring === resumes) stoppedForNoSession = true
      })
      .catch(() => {
        // A lapsed session surfaces on the member's next real request.
      })
  }

  function resume(): void {
    resumes++
    stoppedForNoSession = false
    send()
  }

  return {
    notifyActivity() {
      if (stoppedForNoSession) return
      send()
    },
    notifyNavigation: resume,
    notifySignIn: resume
  }
}
